import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";
import { trpc } from "@utils/api";
import { type inferRouterOutputs, type inferRouterInputs } from "@trpc/server";
import { type AppRouter } from "@server/api/root";
import {
  FrequencyInterval,
  PriceRule,
  type ResourceStatus,
  SessionCreationMode,
} from "skedyul-db/enums";

type RouterOutput = inferRouterOutputs<AppRouter>;
type RouterInput = inferRouterInputs<AppRouter>;
type Service = RouterOutput["services"]["getServices"]["records"][number];
export type NewService = RouterInput["services"]["createService"] & {
  id?: string;
};

export const calculateMaxDeposit = (
  price: number,
  priceRule: PriceRule,
  minSessionLengthInMinutes: number,
  sessionFrequency: number,
): number => {
  if (priceRule === PriceRule.PER_HOUR) {
    return price * (sessionFrequency * (minSessionLengthInMinutes / 60));
  }
  if (priceRule === PriceRule.PER_30_MINUTES) {
    return price * (sessionFrequency * (minSessionLengthInMinutes / 30));
  }
  if (priceRule === PriceRule.PER_15_MINUTES) {
    return price * (sessionFrequency * (minSessionLengthInMinutes / 15));
  }
  if (priceRule === PriceRule.PER_10_MINUTES) {
    return price * (sessionFrequency * (minSessionLengthInMinutes / 10));
  }
  if (priceRule === PriceRule.PER_5_MINUTES) {
    return price * (sessionFrequency * (minSessionLengthInMinutes / 5));
  }
  if (priceRule === PriceRule.PER_MINUTE) {
    return price * (sessionFrequency * minSessionLengthInMinutes);
  }

  return price * sessionFrequency;
};

export type FieldErrorCode = "REQUIRED" | "INVALID" | "OUT_OF_RANGE";

export interface ServiceState {
  service: {
    isLoading: boolean;
    isSaving: boolean;
    data: NewService | null;
    fieldErrors: {
      name?: FieldErrorCode;
      price?: FieldErrorCode;
      deposit?: FieldErrorCode;
    };
  };
  services: {
    isLoading: boolean;
    data: Service[];
    meta: {
      total: number;
      page: number;
      pages: number;
      limit: number;
    };
  };
}

type StringEditableFields = "name" | "description" | "color";
type NumberEditableFields =
  | "maxAttendees"
  | "maxSessionLengthInMinutes"
  | "minSessionLengthInMinutes"
  | "sessionFrequency";
type AmountEditableFields = "price" | "deposit";

const initialState: ServiceState = {
  service: {
    isLoading: false,
    isSaving: false,
    data: {
      name: "",
      description: "",
      color: "#000000",
      approvalRequired: false,
      sessionCreationMode: SessionCreationMode.UNRESTRICTED,
      maxAttendees: null,
      maxSessionLengthInMinutes: 0,
      enableWaitList: false,
      priceRule: PriceRule.PER_SESSION,
      minSessionLengthInMinutes: 60,
      price: {
        amount: 0,
        currency: "AUD",
      },
      deposit: {
        amount: 0,
        currency: "AUD",
      },
      status: "ACTIVE",
      sessionFrequency: 1,
      sessionInterval: FrequencyInterval.ONE_TIME,
    },
    fieldErrors: {},
  },
  services: {
    isLoading: false,
    data: [],
    meta: {
      total: 0,
      page: 0,
      pages: 0,
      limit: 25,
    },
  },
};

export const getService = createAsyncThunk(
  "service/get",
  async (payload: string): Promise<NewService | null> => {
    if (payload == "new") {
      return await trpc.services.getNewService.query();
    }
    return await trpc.services.getService.query(payload);
  },
);

export const getServices = createAsyncThunk(
  "service/getList",
  async (payload: { q: string; limit: string; page: string }) => {
    return await trpc.services.getServices.query({
      limit: Number(payload.limit) || 25,
      page: Number(payload.page) || 0,
      q: payload.q,
    });
  },
);

export const createService = createAsyncThunk(
  "service/create",
  async (payload: NewService) => {
    if (!payload.name) {
      throw new Error("Name is required");
      return null;
    }

    return await trpc.services.createService.mutate(payload);
  },
);

export const updateService = createAsyncThunk(
  "service/update",
  async (payload: NewService & { id: string }) => {
    if (!payload.name) {
      throw new Error("Name is required");
      return null;
    }

    return await trpc.services.updateService.mutate(payload);
  },
);

export const serviceSlice = createSlice({
  name: "service",
  initialState,
  reducers: {
    updateStatus(state, action: PayloadAction<ResourceStatus>) {
      if (state.service.data) {
        state.service.data.status = action.payload;
      }
    },
    updateName(state, action: PayloadAction<string>) {
      if (state.service.data) {
        state.service.data.name = action.payload;

        if (!action.payload) {
          state.service.fieldErrors.name = "REQUIRED";
        } else {
          state.service.fieldErrors.name = undefined;
        }
      }
    },
    updatePriceRule(state, action: PayloadAction<PriceRule>) {
      if (state.service.data) {
        state.service.data.priceRule = action.payload;
        const maxDeposit = calculateMaxDeposit(
          state.service.data.price.amount,
          action.payload,
          state.service.data.minSessionLengthInMinutes,
          state.service.data.sessionFrequency,
        );

        if (state.service.data.deposit.amount > maxDeposit) {
          state.service.data.deposit.amount = maxDeposit;
        }
      }
    },
    updateDeposit(state, action: PayloadAction<number>) {
      if (state.service.data) {
        const maxDeposit = calculateMaxDeposit(
          state.service.data.price.amount,
          state.service.data.priceRule,
          state.service.data.minSessionLengthInMinutes,
          state.service.data.sessionFrequency,
        );

        if (action.payload > maxDeposit) {
          state.service.data.deposit.amount = maxDeposit;
        } else if (action.payload < 0) {
          state.service.data.deposit.amount = 0;
        } else {
          state.service.data.deposit.amount = action.payload;
        }
      }
    },
    updateNumberOfSessions(state, action: PayloadAction<number>) {
      if (state.service.data) {
        state.service.data.sessionFrequency = action.payload;
        const maxDeposit = calculateMaxDeposit(
          state.service.data.price.amount,
          state.service.data.priceRule,
          state.service.data.minSessionLengthInMinutes,
          action.payload,
        );

        if (state.service.data.deposit.amount > maxDeposit) {
          state.service.data.deposit.amount = maxDeposit;
        }
      }
    },
    updateSessionCreationMode(
      state,
      action: PayloadAction<SessionCreationMode>,
    ) {
      if (state.service.data) {
        state.service.data.sessionCreationMode = action.payload;
      }
    },
    updateFrequencyInterval(state, action: PayloadAction<FrequencyInterval>) {
      if (state.service.data) {
        state.service.data.sessionInterval = action.payload;
      }
    },
    toggleAttendeeLimit(state, action: PayloadAction<boolean>) {
      if (state.service.data) {
        if (action.payload) {
          state.service.data.maxAttendees = 1;
        } else {
          state.service.data.maxAttendees = null;
        }
      }
    },
    updateStringField(
      state,
      action: PayloadAction<{ id: StringEditableFields; value: string }>,
    ) {
      if (state.service.data) {
        state.service.data[action.payload.id] = action.payload.value;
      }
    },
    updateNumberField(
      state,
      action: PayloadAction<{ id: NumberEditableFields; value: number }>,
    ) {
      if (state.service.data) {
        state.service.data[action.payload.id] = action.payload.value;
      }
    },
    updateAmountField(
      state,
      action: PayloadAction<{
        id: AmountEditableFields;
        value: NewService["price"];
      }>,
    ) {
      if (state.service.data) {
        state.service.data[action.payload.id] = action.payload.value;
      }
    },
    updateBooleanField(
      state,
      action: PayloadAction<{
        id: "approvalRequired" | "enableWaitList";
        value: boolean;
      }>,
    ) {
      if (state.service.data) {
        state.service.data[action.payload.id] = action.payload.value;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getService.pending, (state) => {
      state.service.isLoading = true;
    });
    builder.addCase(getService.fulfilled, (state, action) => {
      state.service.isLoading = false;
      state.service.data = action.payload;
      state.service.fieldErrors = {};
    });
    builder.addCase(getService.rejected, (state) => {
      state.service.isLoading = false;
      state.service.data = null;
    });
    builder.addCase(getServices.pending, (state) => {
      state.services.isLoading = true;
    });
    builder.addCase(getServices.fulfilled, (state, action) => {
      state.services.isLoading = false;
      state.services.data = action.payload.records;
      state.services.meta = action.payload.meta;
    });
    builder.addCase(getServices.rejected, (state) => {
      state.services.isLoading = false;
      state.services.data = [];
    });
    builder.addCase(createService.pending, (state) => {
      state.service.isSaving = true;
    });
    builder.addCase(createService.fulfilled, (state, action) => {
      state.service.isSaving = false;
      state.service.data = action.payload;
    });
    builder.addCase(createService.rejected, (state) => {
      state.service.isSaving = false;
      state.service.fieldErrors = {
        name: "REQUIRED",
      };
    });
    builder.addCase(updateService.pending, (state) => {
      state.service.isSaving = true;
    });
    builder.addCase(updateService.fulfilled, (state, action) => {
      state.service.isSaving = false;
      state.service.data = action.payload;
    });
    builder.addCase(updateService.rejected, (state) => {
      state.service.isSaving = false;
      state.service.fieldErrors = {
        name: "REQUIRED",
      };
    });
  },
});

// Action creators are generated for each case reducer function
export const {
  updateStringField,
  updateAmountField,
  updateNumberField,
  updateBooleanField,
  updateStatus,
  updatePriceRule,
  updateSessionCreationMode,
  updateFrequencyInterval,
  toggleAttendeeLimit,
  updateNumberOfSessions,
  updateDeposit,
  updateName,
} = serviceSlice.actions;

export default serviceSlice.reducer;
